import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, HeadphonesIcon as Headset } from 'lucide-react';

const ContactHeroBit = ({ isMobile = false }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pearl via-pearl to-nude/30 p-6 md:p-12"
        >
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239CAF96' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                    backgroundSize: '100px 100px'
                }} />
            </div>

            <div className="relative z-10">
                {/* Icon Row */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={`flex ${isMobile ? 'justify-center' : 'justify-start'} gap-4 mb-6`}
                >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-champagne" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sage/20 to-champagne/20 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-sage" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <Headset className="w-5 h-5 text-champagne" />
                    </div>
                </motion.div>

                {/* Title */}
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className={`font-heading font-light text-charcoal mb-4 ${
                        isMobile ? 'text-3xl text-center' : 'text-4xl md:text-5xl'
                    }`}
                >
                    We are here for your<br className="hidden md:block" /> KROMAIC ritual
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    className={`text-charcoal/60 ${
                        isMobile ? 'text-center text-base' : 'text-lg md:text-xl max-w-2xl'
                    }`}
                >
                    Our beauty advisors are ready to support you with personalized guidance,
                    product recommendations, and any questions about your ritual.
                </motion.p>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 ${
                        isMobile ? 'max-w-md mx-auto' : ''
                    }`}
                >
                    {[
                        { value: '24/7', label: 'Email Support' },
                        { value: '30-min', label: 'Response Time' },
                        { value: '100%', label: 'Cruelty Free' },
                        { value: '95%', label: 'Satisfaction' }
                    ].map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            className="text-center p-4 rounded-xl bg-white/50 backdrop-blur-sm"
                        >
                            <div className="text-xl md:text-2xl font-light text-charcoal mb-1">{stat.value}</div>
                            <div className="text-xs md:text-sm text-charcoal/50">{stat.label}</div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            {/* Floating Elements */}
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className={`absolute ${isMobile ? 'right-4 top-4 w-16 h-16' : 'right-8 top-8 w-24 h-24'} opacity-5`}
            >
                <svg viewBox="0 0 100 100" className="w-full h-full text-sage">
                    <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="1" fill="none" />
                </svg>
            </motion.div>
        </motion.div>
    );
};

export default ContactHeroBit;